import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Award, TrendingUp, AlertTriangle, BookOpen, FileText, Users, Calendar, Filter, ChevronRight, Lightbulb, Activity, ClipboardList, Clock } from "lucide-react";

const monthISO = () => new Date().toISOString().slice(0, 7);

const RISK_META = {
    high: { label: "Rrezik i Lartë", chip: "bg-terracotta/10 text-terracotta border-terracotta/30" },
    medium: { label: "Rrezik Mesatar", chip: "bg-amber-50 text-amber-700 border-amber-200" },
    improving: { label: "Në Përmirësim", chip: "bg-seablue/10 text-seablue border-seablue/30" },
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = "oxford", testId }) => {
    const accents = {
        oxford: "bg-oxford/10 text-oxford",
        sage: "bg-sage/10 text-sage",
        terracotta: "bg-terracotta/10 text-terracotta",
        amber: "bg-amber-50 text-amber-700",
        seablue: "bg-seablue/10 text-seablue",
    };
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
            <div className="flex items-start justify-between mb-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accents[accent]}`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className="font-heading text-2xl sm:text-3xl font-black text-slate-900 mt-1 leading-none">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
};

export default function QualityDashboard() {
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [teacherList, setTeacherList] = useState([]);
    const [overview, setOverview] = useState(null);
    const [planSummary, setPlanSummary] = useState(null);
    const [quarterlyPlans, setQuarterlyPlans] = useState([]);
    const [lessonStats, setLessonStats] = useState(null);
    const [filters, setFilters] = useState({ teacher_id: "", subject_id: "", class_id: "", month: monthISO() });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const [t, s, c] = await Promise.all([
                api.get("/admin/teachers"),
                api.get("/subjects"),
                api.get("/classes"),
            ]);
            setTeachers(t.data); setSubjects(s.data); setClasses(c.data);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
                const [o, tl, sp, qp, ls] = await Promise.all([
                    api.get("/quality/overview", { params }),
                    api.get("/quality/teachers", { params: { month: filters.month } }),
                    api.get("/quality/student-plans-summary"),
                    api.get("/teacher/quarterly-plans", { params: filters.teacher_id ? { teacher_id: filters.teacher_id } : {} }),
                    api.get("/quality/lesson-stats", { params: filters.teacher_id ? { teacher_id: filters.teacher_id, month: filters.month } : { month: filters.month } }),
                ]);
                setOverview(o.data);
                setTeacherList(tl.data);
                setPlanSummary(sp.data);
                setQuarterlyPlans(qp.data);
                setLessonStats(ls.data);
            } finally { setLoading(false); }
        })();
    }, [filters]);

    const selectedTeacher = useMemo(() => teachers.find((t) => t.id === filters.teacher_id), [teachers, filters.teacher_id]);

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-2">
                    Koordinatori i Cilësisë
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-seablue/15 text-seablue text-[10px] font-bold">
                        <Activity className="w-3 h-3" /> MONITORIM
                    </span>
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Paneli i Cilësisë</h1>
                <p className="text-slate-600 mt-2 text-sm">Monitoro performancën e profesorëve, nxënësit në rrezik dhe materialet e postuara — {overview?.month}</p>
            </div>

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 mb-5 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Filter className="w-3 h-3" /> Profesori</label>
                    <select value={filters.teacher_id} onChange={(e) => setFilters({ ...filters, teacher_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="q-filter-teacher">
                        <option value="">Të gjithë profesorët</option>
                        {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><BookOpen className="w-3 h-3" /> Lënda</label>
                    <select value={filters.subject_id} onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="q-filter-subject">
                        <option value="">Të gjitha lëndët</option>
                        {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Klasa</label>
                    <select value={filters.class_id} onChange={(e) => setFilters({ ...filters, class_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="q-filter-class">
                        <option value="">Të gjitha klasat</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Muaji</label>
                    <input type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2 font-mono" data-testid="q-filter-month" />
                </div>
            </div>

            {loading || !overview ? <div className="text-slate-500">Duke ngarkuar…</div> : (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                        <KpiCard icon={FileText} label="Materiale Postuara" value={overview.kpis.materials_total} sub={`${overview.kpis.kind_counts.plan || 0} plane, ${overview.kpis.kind_counts.quiz || 0} quiz, ${overview.kpis.kind_counts.worksheet || 0} worksheet`} accent="oxford" testId="kpi-materials" />
                        <KpiCard icon={TrendingUp} label="Progresi i Klasës" value={`${overview.kpis.class_avg}%`} sub={`${overview.kpis.students_total} nxënës total`} accent="sage" testId="kpi-progress" />
                        <KpiCard icon={AlertTriangle} label="Nxënës në Rrezik" value={overview.kpis.at_risk_total} sub={`${overview.kpis.high_risk} lartë · ${overview.kpis.medium_risk} mesatar`} accent="terracotta" testId="kpi-at-risk" />
                        <KpiCard icon={Award} label="Vlerësim Profesori" value={`${overview.kpis.teacher_rating}/100`} sub={selectedTeacher ? selectedTeacher.name : "Mesatare e të gjithë profesorëve"} accent="amber" testId="kpi-rating" />
                        <KpiCard icon={Calendar} label="Mungesa Profesori" value={overview.kpis.teacher_absences} sub={selectedTeacher ? `${selectedTeacher.name} këtë muaj` : "Zgjidh profesor"} accent="seablue" testId="kpi-teacher-abs" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                        {/* Materials */}
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden lg:col-span-2">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-oxford" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Çfarë ka postuar profesori</div>
                                <div className="ml-auto text-xs text-slate-500">{overview.materials.length} materiale</div>
                            </div>
                            {overview.materials.length === 0 ? (
                                <div className="p-8 text-center text-slate-500 text-sm">Asnjë material i postuar në periudhën e zgjedhur.</div>
                            ) : (
                                <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
                                    {overview.materials.map((m) => (
                                        <div key={m.id} className="px-4 sm:px-5 py-3 hover:bg-bone/30" data-testid={`q-material-${m.id}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-bold text-slate-900 text-sm truncate">{m.title}</span>
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-oxford/10 text-oxford text-[10px] font-bold shrink-0">{m.subject_name}</span>
                                            </div>
                                            <div className="text-[11px] text-slate-500 flex flex-wrap gap-2">
                                                <span>{m.teacher_name}</span>
                                                <span>•</span>
                                                <span>{m.class_name}</span>
                                                <span>•</span>
                                                <span className="font-mono">{m.created_at?.slice(0, 10)}</span>
                                                {(m.attachments?.length || 0) > 0 && <><span>•</span><span className="text-sage">{m.attachments.length} skedar</span></>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Recommendation card */}
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-700 flex items-center justify-center">
                                    <Lightbulb className="w-4 h-4" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-amber-800">Rekomandim Automatik</div>
                            </div>
                            <ul className="space-y-2" data-testid="q-recommendations">
                                {overview.recommendations.map((r, i) => (
                                    <li key={i} className="text-sm text-slate-800 leading-relaxed flex gap-2">
                                        <span className="text-amber-600 font-bold shrink-0">{i + 1}.</span>
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Plan A/B/C Summary + Quarterly Plans */}
                    {planSummary && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                                <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                    <Lightbulb className="w-4 h-4 text-oxford" />
                                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Planet A / B / C</div>
                                    <div className="ml-auto text-xs text-slate-500">{planSummary.total_plans}</div>
                                </div>
                                <div className="p-5 grid grid-cols-3 gap-2">
                                    <div className="text-center p-3 rounded-lg bg-sage/5 border border-sage/30" data-testid="q-plan-a">
                                        <div className="font-heading text-3xl font-black text-sage">{planSummary.totals.A}</div>
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-sage">Plan A</div>
                                        <div className="text-[10px] text-slate-500">E lehtë</div>
                                    </div>
                                    <div className="text-center p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="q-plan-b">
                                        <div className="font-heading text-3xl font-black text-amber-700">{planSummary.totals.B}</div>
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Plan B</div>
                                        <div className="text-[10px] text-slate-500">Moderate</div>
                                    </div>
                                    <div className="text-center p-3 rounded-lg bg-terracotta/10 border border-terracotta/30" data-testid="q-plan-c">
                                        <div className="font-heading text-3xl font-black text-terracotta">{planSummary.totals.C}</div>
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">Plan C</div>
                                        <div className="text-[10px] text-slate-500">Intensive</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden lg:col-span-2">
                                <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                    <ClipboardList className="w-4 h-4 text-oxford" />
                                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Planet Tremujore Tremujore (Word)</div>
                                    <div className="ml-auto text-xs text-slate-500">{quarterlyPlans.length}</div>
                                </div>
                                {quarterlyPlans.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 text-sm">Asnjë plan i dorëzuar nga ky filtër.</div>
                                ) : (
                                    <div className="divide-y divide-slate-200 max-h-72 overflow-y-auto">
                                        {quarterlyPlans.map((p) => {
                                            const total = p.units.length * 3;
                                            const done = p.units.reduce((acc, u) => acc + (u.worksheet_delivered ? 1 : 0) + (u.quiz_delivered ? 1 : 0) + (u.test_delivered ? 1 : 0), 0);
                                            const pct = total === 0 ? 0 : Math.round((done / total) * 100);
                                            return (
                                                <div key={p.id} className="p-3 px-4 sm:px-5" data-testid={`q-qp-${p.id}`}>
                                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                                        <span className="font-bold text-slate-900 text-sm">{p.subject_name}</span>
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-oxford/10 text-oxford font-bold">{p.class_name}</span>
                                                        <span className="text-[10px] text-slate-500">• {p.period} • {p.year}</span>
                                                        <span className="ml-auto text-[10px] text-slate-500">{p.teacher_name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-sage" style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="text-[11px] font-bold text-slate-600">{pct}% • {p.units.length} njësi</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Lesson Attendance Stats */}
                    {lessonStats && lessonStats.totals.lessons > 0 && (
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-oxford" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Orët e mësimdhënies — {lessonStats.month}</div>
                                <div className="ml-auto text-xs text-slate-500">{lessonStats.totals.lessons} orë</div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
                                <div className="bg-sage/5 border border-sage/30 rounded-lg p-3 text-center" data-testid="ls-completed">
                                    <div className="font-heading text-2xl font-black text-sage">{lessonStats.totals.completed}</div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-sage">Të Përfunduara</div>
                                </div>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="ls-progress">
                                    <div className="font-heading text-2xl font-black text-amber-700">{lessonStats.totals.in_progress}</div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Në Vazhdim</div>
                                </div>
                                <div className="bg-oxford/5 border border-oxford/20 rounded-lg p-3 text-center" data-testid="ls-actual">
                                    <div className="font-heading text-2xl font-black text-oxford">{lessonStats.totals.actual_minutes}min</div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-oxford">Min reale</div>
                                    <div className="text-[10px] text-slate-500">/ {lessonStats.totals.planned_minutes}min plan</div>
                                </div>
                                <div className={`rounded-lg p-3 text-center border ${lessonStats.totals.late_minutes > 30 ? "bg-terracotta/10 border-terracotta/30" : "bg-slate-50 border-slate-200"}`} data-testid="ls-late">
                                    <div className={`font-heading text-2xl font-black ${lessonStats.totals.late_minutes > 30 ? "text-terracotta" : "text-slate-700"}`}>{lessonStats.totals.late_minutes}min</div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Vonesa Total</div>
                                </div>
                            </div>
                            {lessonStats.by_teacher.length >= 1 && !filters.teacher_id && (
                                <div className="px-4 pb-4">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Sipas profesorit</div>
                                    <div className="space-y-1.5">
                                        {lessonStats.by_teacher.slice(0, 5).map((t) => (
                                            <div key={t.teacher_id} className="flex items-center gap-2 text-xs" data-testid={`ls-teacher-${t.teacher_id}`}>
                                                <span className="font-bold text-slate-800 w-40 truncate">{t.teacher_name}</span>
                                                <span className="text-slate-500">{t.completed}/{t.lessons} orë</span>
                                                <span className="text-oxford font-mono">{t.actual_min}min</span>
                                                {t.late_min > 0 && <span className="text-terracotta font-mono">+{t.late_min}min vonesë</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* At-risk students */}
                    {overview.at_risk.length > 0 && (
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-terracotta" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Nxënës në Rrezik</div>
                                <div className="ml-auto text-xs text-slate-500">{overview.at_risk.length} nxënës</div>
                            </div>
                            <div className="divide-y divide-slate-200">
                                {overview.at_risk.map((s) => {
                                    const meta = RISK_META[s.level];
                                    return (
                                        <div key={s.id} className="px-4 sm:px-5 py-3 flex items-center gap-3" data-testid={`q-risk-${s.id}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 text-xs ${s.level === "high" ? "bg-terracotta/10 text-terracotta" : s.level === "medium" ? "bg-amber-100 text-amber-700" : "bg-seablue/10 text-seablue"}`}>
                                                {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-slate-900 truncate text-sm">{s.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    Mesatarja: <strong className="text-oxford">{s.avg}%</strong> • {s.absences} mungesa
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-bold ${meta.chip}`}>{meta.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Teacher ranking */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                            <Award className="w-4 h-4 text-oxford" />
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Profesorët — Renditje e Muajit</div>
                            <div className="ml-auto text-xs text-slate-500">{teacherList.length}</div>
                        </div>
                        <div className="divide-y divide-slate-200 max-h-[500px] overflow-y-auto">
                            {teacherList.map((t, i) => {
                                const tier = t.rating >= 70 ? "sage" : t.rating >= 45 ? "amber" : "terracotta";
                                return (
                                    <button key={t.id} onClick={() => setFilters({ ...filters, teacher_id: filters.teacher_id === t.id ? "" : t.id })} className={`w-full text-left px-4 sm:px-5 py-3 hover:bg-bone/40 flex items-center gap-3 ${filters.teacher_id === t.id ? "bg-oxford/5" : ""}`} data-testid={`q-teacher-${t.id}`}>
                                        <div className="w-8 text-center text-[11px] font-bold text-slate-400">#{i + 1}</div>
                                        <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs shrink-0">
                                            {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate text-sm">{t.name}</div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {t.materials} materiale • {t.absences} mungesa • {t.subjects_assigned} lëndë caktuar
                                            </div>
                                        </div>
                                        <div className={`text-right shrink-0 ${tier === "sage" ? "text-sage" : tier === "amber" ? "text-amber-600" : "text-terracotta"}`}>
                                            <div className="font-heading text-lg font-black leading-none">{t.rating}</div>
                                            <div className="text-[10px] uppercase tracking-widest font-bold opacity-75">/ 100</div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-300" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </Layout>
    );
}
